import { gql, useQuery } from '@apollo/client';
import IconGraphQL from '@apollo/icons/default/IconGraphQL.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
} from '@apollo/orbit';
import React, { useEffect, useState } from 'react';

import { contractsConfigRoute } from 'src/app/graph/routes';
import { Form as FormWrapper } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  ContractBasicDetailsQuery,
  ContractBasicDetailsQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { ContractConfigHeader } from '../ContractConfigHeader';
import { ContractConfigStep, ContractConfigWorkflow } from '../steps';

export interface ContractBasicDetailsModalState {
  graphId?: string;
  contractVariant?: string;
  sourceVariant?: string;
}

interface Props {
  isOpen: boolean;
  state: ContractBasicDetailsModalState | null;
  onGraphIdChange: (graphId: string) => void;
  onContractVariantChange: (contracVariant: string) => void;
  onSourceVariantChange: (sourceVariant: string) => void;
  onGraphTitleChange: (graphTitle: string) => void;
  onComplete: () => void;
  onClose: () => void;
}

const contractBasicDetailsQuery = gql<
  ContractBasicDetailsQuery,
  ContractBasicDetailsQueryVariables
>`
  query ContractBasicDetailsQuery($graphId: ID!) {
    service(id: $graphId) {
      id
      title
      variants {
        id
        name
        isContract
        compositionVersion
        supportedDirectives {
          enabled
          name
        }
      }
    }
  }
`;

const variantNameRegex = /^[a-zA-Z0-9][\/.a-zA-Z0-9_-]{0,63}$/;

function CompositionVersionWarning({
  fedVersion,
  routerVersion,
}: {
  fedVersion: '1' | '2' | '2.1';
  routerVersion: string;
}) {
  return (
    <Alert status="warning" className="-mb-2 mt-2">
      Federation {fedVersion} contract variants are only supported in newer
      versions of Apollo Router. Please ensure that you are running router
      versions <span className="font-semibold">&gt;= {routerVersion}</span> for
      this contract variant to avoid any runtime errors.
    </Alert>
  );
}

export const ContractBasicDetailsModal = ({
  isOpen,
  state,
  onGraphIdChange,
  onSourceVariantChange,
  onContractVariantChange,
  onGraphTitleChange,
  onComplete,
  onClose,
}: Props) => {
  const { graphId: graphIdFromUrl, sourceVariant: sourceVariantFromUrl } =
    useRouteParams(contractsConfigRoute);

  const { data, error } = useQuery(contractBasicDetailsQuery, {
    variables: { graphId: graphIdFromUrl },
    skip: !graphIdFromUrl,
  });

  const [contractVariantError, setContractVariantError] = useState<
    string | undefined
  >();

  const [compositionVersion, setCompositionVersion] = useState('');

  useEffect(() => {
    if (!state?.graphId && graphIdFromUrl) {
      onGraphIdChange(graphIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.graphId, graphIdFromUrl]);

  useEffect(() => {
    if (!state?.sourceVariant && sourceVariantFromUrl) {
      onSourceVariantChange(sourceVariantFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.sourceVariant, sourceVariantFromUrl]);

  useEffect(() => {
    if (data?.service?.variants && sourceVariantFromUrl) {
      const compVersion =
        data.service.variants.find((v) => v.name === sourceVariantFromUrl)
          ?.compositionVersion ?? '';
      setCompositionVersion(compVersion);
    }
  }, [sourceVariantFromUrl, data?.service?.variants]);

  useEffect(() => {
    if (data?.service?.title) {
      onGraphTitleChange(data.service.title);
    }
    // Don't depend on the callback function changing here
    // since it results in an infinite render loop.
    // Ultimately we probably want to use a pattern where the wizard
    // can easily memoize these functions so they don't change on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.service?.title]);

  const contractVariantsWithTagDirectiveEnabled =
    data?.service?.variants.filter(
      (variant) =>
        !variant.isContract &&
        !!variant.supportedDirectives?.find(
          (dir) => dir.name === 'tag' && dir.enabled,
        ),
    );

  return (
    <Modal isOpen={isOpen} size="2xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper
        onSubmit={() => {
          if (!state?.contractVariant) {
            setContractVariantError('Variant name is required');
            return;
          }
          if (!variantNameRegex.test(state.contractVariant)) {
            setContractVariantError(
              'Variant names must contain only letters, numbers or -/_',
            );
            return;
          }
          if (
            data?.service?.variants.find(
              (variant) => variant.name === state.contractVariant,
            )
          ) {
            setContractVariantError(
              'Variant name is already in use for this graph.',
            );
            return;
          }
          onComplete();
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ContractConfigHeader
              currentStep={ContractConfigStep.BasicDetails}
              workflow={ContractConfigWorkflow.create}
            />
            <ModalTitle>Create a New Contract</ModalTitle>
            <ModalDescription className="flex-row">
              With contracts you can setup filters to include or exclude
              specific types and fields in your schema.{' '}
              <Link
                isExternal
                to="https://www.apollographql.com/docs/studio/contracts"
              >
                Learn more
              </Link>
              {compositionVersion === '1' && (
                <CompositionVersionWarning fedVersion="1" routerVersion="0.9" />
              )}
              {compositionVersion === '2' && (
                <CompositionVersionWarning fedVersion="2" routerVersion="0.9" />
              )}
              {compositionVersion === '2.1' && (
                <CompositionVersionWarning
                  fedVersion="2.1"
                  routerVersion="1.2.1"
                />
              )}
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {error && (
              <ErrorMessage className="mb-10">
                Something went wrong. Please refresh and try again.
              </ErrorMessage>
            )}
            <FormControl isRequired>
              <FormLabel>Graph</FormLabel>
              <Input
                value={data?.service?.title ?? ''}
                leftElement={<IconGraphQL className="text-indigo-light" />}
                isDisabled
              />
            </FormControl>
            <FormControl
              isRequired
              isInvalid={!!contractVariantError}
              id="contractVariant"
            >
              <FormLabel>Contract Variant Name</FormLabel>
              <Input
                placeholder="e.g. public, ios-dev, internal, etc."
                autoFocus
                value={state?.contractVariant ?? ''}
                onChange={(evt) => {
                  setContractVariantError(undefined);
                  onContractVariantChange(evt.target.value);
                }}
              />
              <FormErrorMessage>{contractVariantError}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired id="sourceVariant">
              <FormLabel>Source Variant</FormLabel>
              <FormHelperText>
                The graph variant that contract filters will be applied to.
              </FormHelperText>
              <Select
                value={state?.sourceVariant}
                onChange={(evt) => {
                  const sourceVariant = evt.target.value;
                  const compVersion =
                    contractVariantsWithTagDirectiveEnabled?.find(
                      (v) => v.name === sourceVariant,
                    )?.compositionVersion ?? '';
                  setCompositionVersion(compVersion);
                  onSourceVariantChange(sourceVariant);
                }}
                placeholder="Choose a source variant"
                isDisabled={
                  !isMinLengthArray(1, contractVariantsWithTagDirectiveEnabled)
                }
              >
                {contractVariantsWithTagDirectiveEnabled?.map((variant) => (
                  <option key={variant.id} value={variant.name}>
                    {variant.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>
                Your graph does not have any variants that support the{' '}
                <span className="font-mono text-sm">@tag</span> directive.
              </FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={
                  !state?.sourceVariant ||
                  !state.contractVariant ||
                  !!contractVariantError
                }
              >
                Continue
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
